import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';

// User Perception & Experience Analysis Categories
const PERCEPTION_CATEGORIES = [
  { 
    code: 'groundedness', 
    name: 'Groundedness Check', 
    description: 'Verify AI responses are grounded in the provided context and not making unsupported claims',
  },
  { 
    code: 'hallucination', 
    name: 'Hallucination Detection', 
    description: 'Detect made-up facts, false information, or content not present in the source material',
  },
  { 
    code: 'tone', 
    name: 'Appropriate Tone Check', 
    description: 'Ensure AI response tone is appropriate and matches the context/query intent',
  },
];

interface SemanticCategorySelectorProps {
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

export const SemanticCategorySelector: React.FC<SemanticCategorySelectorProps> = ({
  value = [],
  onChange,
  disabled = false
}) => {
  const handleToggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter(c => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Ignored User Perception Categories
        <Tooltip title="Select categories that should be ignored during user perception analysis. This allows you to skip checks that aren't relevant to your API (e.g., skip tone check for factual APIs).">
          <Chip 
            label="?" 
            size="small" 
            sx={{ ml: 1, height: 20, fontSize: '0.7rem', cursor: 'help' }}
            tabIndex={0}
          />
        </Tooltip>
      </Typography>
      
      {value.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} {value.length === 1 ? 'category' : 'categories'} will be ignored during user perception analysis
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <FormGroup>
          {PERCEPTION_CATEGORIES.map((category) => (
            <Tooltip key={category.code} title={category.description} placement="right">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includes(category.code)}
                    onChange={() => handleToggle(category.code)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      <strong>{category.name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            </Tooltip>
          ))}
        </FormGroup>
      </Paper>

      {value.length === PERCEPTION_CATEGORIES.length && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="warning.main">
            Warning: All categories are ignored. No user perception analysis will affect pass/fail status.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SemanticCategorySelector;

